class BaseProductComponent extends HTMLElement {
  constructor() {
    super();

    this.productMain = this.closest('product-main');
  }

  connectedCallback() {
    this.productMain?.register(this);
  }

  disconnectedCallback() {
    this.productMain?.unregister(this);
  }

  validate() {
    return true;
  }
}

if (!customElements.get('product-main')) {
  customElements.define('product-main', class ProductMain extends HTMLElement {
    constructor() {
      super();

      this.tableRows = {
        units: this.querySelector('tr#quantity-and-price__table-units'),
        prices: this.querySelector('tr#quantity-and-price__table-price'),
        discounts: this.querySelector('tr#quantity-and-price__table-discount'),
      };
      this.select = this.querySelector('[name="options[Quantity]"]');
      this.options = this.querySelectorAll('[name="options[Quantity]"] option');

      this._components = new Set();
    }

    register(component) {
      this._components.add(component);
    }

    unregister(component) {
      this._components.delete(component);
    }

    validateAll() {
      let allValid = true;

      for (const component of this._components) {
        if (typeof component.validate !== 'function') continue;

        try {
          const ok = component.validate();
          if (!ok) allValid = false;
        } catch (err) {
          console.error('validate error in', component.tagName?.toLowerCase(), err);
          allValid = false;
        }
      }

      return allValid;
    }
  });
}

if (!customElements.get('product-variations')) {
  customElements.define('product-variations', class ProductVariations extends BaseProductComponent {
    constructor() {
      super();

      this.bindEvents();
    }

    bindEvents() {
      this.addEventListener('click', (e) => {
        const variation = e.target.closest('a');
        if (!variation || !this.contains(variation)) return;

        e.preventDefault();
        this.fetchSection(variation);
      });
    }

    fetchSection(variation) {
      const sectionId = this.dataset.sectionId;
      const base = new URL(variation.getAttribute('href'), location.origin);

      new URLSearchParams(location.search).forEach((v, k) => base.searchParams.set(k, v));
      base.searchParams.delete("sections");

      const url = base.toString();
      const fetchBase = new URL(url);
      fetchBase.searchParams.set("sections", sectionId);
      const fetchUrl = fetchBase.toString();

      this.closest('.product__info-container').classList.add('loading');

      fetch(fetchUrl)
        .then(response => response.text())
        .then(text => {
          let json;
          try {
            json = JSON.parse(text);
          } catch (err) {
            console.error("JSON parse error", err, text);
            return;
          }

          const sectionHTML = json[sectionId];

          if (!sectionHTML) {
            console.error(`Section ${sectionId} not found in response`, json);
            return;
          }

          const wishlistWrapper = this.closest('.product').querySelector('.frcp-wishlist-wrapper');
          const temp = document.createElement('div');
          temp.innerHTML = sectionHTML.trim();
          const newSection = temp.firstElementChild;
          const newSectionDOM = document.querySelector(`#shopify-section-${sectionId}`);

          if (newSectionDOM) {
            const currentInfo = document.querySelector('.product__info-container');
            const newInfo = newSection.querySelector('.product__info-container');
            const productTitle = this.getAttribute('data-product-title');
            const newProductVariations = newSection.querySelector('product-variations');
            const newProductTitle = newProductVariations.getAttribute('data-product-title');

            if (newInfo && currentInfo) {
              currentInfo.replaceWith(newInfo);

              if (wishlistWrapper) {
                const newProductId = newProductVariations.getAttribute('data-product-id');
                const newProductHandle = newProductVariations.getAttribute('data-product-handle');
                const newVariantId = newProductVariations.getAttribute('data-product-current-variant-id');

                const wishlistHTML = this.renderWishlistButtonHTML({
                  productId: newProductId,
                  variantId: newVariantId,
                  handle: newProductHandle,
                  title: newProductId
                });

                const form = document.querySelector('.shopify-product-form');
                const wishlistButton = document.createElement('div');

                wishlistButton.classList.add('frcp-wishlist-wrapper');
                wishlistButton.innerHTML = wishlistHTML;

                form.append(wishlistButton);
              }
            } else {
              throw new Error('product__info-container not found');
            }

            document.title = document.title.replace(productTitle, newProductTitle);
            window.history.pushState({}, '', url);

            new DynamicUrl(true).getFromURL();
            document.querySelector('variant-selects')?.dispatchEvent(new Event('change'));
          } else {
            console.warn(`Existing section #shopify-section-${sectionId} not found`);
          }
        })
        .finally(() => {
          document.querySelector('.product__info-container').classList.remove('loading');
        })
        .catch(err => console.error("Fetch error:", err));
    }

    isInWishlist(productId, variantId) {
      const lists = window?.frcp?.wishlist?.data?.product_id_lists?.default;
      if (!lists) return false;

      const variants = lists[String(productId)];
      if (!variants || !Array.isArray(variants)) return false;

      return variants.includes(Number(variantId));
    }

    renderWishlistButtonHTML({productId, variantId, handle, title}) {
      const inWishlist = this.isInWishlist(productId, variantId);

      if (inWishlist) {
        return `
        <span
          tabindex="0"
          aria-label="Remove from wishlist"
          title="Remove from wishlist"
          role="button"
          data-frcp-in-wishlist="true"
          data-frcp-product-handle="${handle}"
          data-frcp-product-id="${productId}"
          data-frcp-variant-id="${variantId}"
          data-frcp-product-title="${title}"
          class="frcp-wishlist-btn frcp-wishlist-product-btn frcp-wishlist-btn--remove"
        >
          <span class="frcp-wishlist-btn__text">Remove from wishlist</span>
          <span class="frcp-wishlist-btn__spinner"></span>
        </span>
      `;
      }

      return `
      <span
        tabindex="0"
        aria-label="Add to wishlist"
        title="Add to wishlist"
        role="button"
        data-frcp-in-wishlist="false"
        data-frcp-product-handle="${handle}"
        data-frcp-product-id="${productId}"
        data-frcp-variant-id="${variantId}"
        data-frcp-product-title="${title}"
        class="frcp-wishlist-btn frcp-wishlist-product-btn frcp-wishlist-btn--add"
      >
        <span class="frcp-wishlist-btn__text">Add to wishlist</span>
        <span class="frcp-wishlist-btn__spinner"></span>
      </span>
    `;
    }
  });
}

if (!customElements.get('roi-calculator')) {
  customElements.define('roi-calculator', class RoiCalculator extends BaseProductComponent {
    constructor() {
      super();

      this.roiLocale = this.dataset.locale;
      this.roiCurrency = this.dataset.currency;
      this.roiPrices = JSON.parse(this.dataset.prices);
      this.roiQuantity = this.querySelector('#roi-quantity');
      this.roiQuantityOptions = this.productMain.querySelectorAll('#roi-quantity option');
      this.roiTotalOrder = this.querySelector('#roi-total-order');
      this.roiUnitPrice = this.querySelector('#roi-unit-price');
      this.roiRetailPrice = this.querySelector('#roi-retail-price');
      this.roiMargin = this.querySelector('#roi-margin');
      this.roiMarkup = this.querySelector('#roi-markup');
      this.roiRevenue = this.querySelector('#roi-revenue');
      this.roiProfit = this.querySelector('#roi-profit');
      this.roiBreakeven = this.querySelector('#roi-breakeven');
      this.roiQuantityValues = [];
      this.initialUnitPrice = null;

      this.formatter = new Intl.NumberFormat(this.roiLocale || 'en-US', {
        style: 'currency',
        currency: this.roiCurrency || 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      this.bindEvents();
      this.init();
    }

    bindEvents() {
      // UPDATES RESULTS WHEN CHANGING QUANTITY
      this.roiQuantity.addEventListener("change", this.computeRoi.bind(this));
      // UPDATES RESULTS WHEN UPDATING THE RETAIL PRICE
      this.roiRetailPrice.addEventListener("keyup", this.computeRoi.bind(this));
    }

    init() {
      // HIDE SAMPLE FROM THE SELECTORS
      this.roiQuantityOptions.forEach(option => {
        if (option.textContent.trim().toLowerCase() === 'sample') {
          option.remove();
        }
      });

      // GET QUANTITY NUMBER ARRAY
      for (const value of this.roiQuantity) {
        const text = value?.textContent?.trim();
        const matches = text ? text.match(/\d+/g) : null;
        if (matches && matches.length > 0) {
          this.roiQuantityValues.push(Number(matches.join("")));
        }
      }

      // CALCULATE & DISPLAY INITIAL VALUES
      let initialTotalOrder = this.roiPrices[0] / 100;
      this.roiTotalOrder.textContent = this.formatMoney(initialTotalOrder);
      this.initialUnitPrice = this.roiPrices[0] / this.roiQuantityValues[0] / 100;
      this.roiUnitPrice.textContent = this.formatMoney(this.initialUnitPrice);
    }

    formatMoney(value) {
      if (value == null || isNaN(value)) return '';

      return this.formatter.format(value);
    }

    computeRoi() {
      let quantityIndex = Number(this.roiQuantity.value);
      let quantity = this.roiQuantityValues[quantityIndex];
      let selectedPrice = this.roiPrices[quantityIndex];
      let retailPrice = parseFloat(this.roiRetailPrice.value);

      let totalOrder = selectedPrice / 100;
      this.roiTotalOrder.textContent = this.formatMoney(totalOrder);
      let unitPrice = selectedPrice / quantity / 100;
      this.roiUnitPrice.textContent = this.formatMoney(unitPrice);

      if (retailPrice > 0) {
        let margin = (1 - unitPrice / retailPrice) * 100
        let markup = (retailPrice / unitPrice) * 100;
        let revenue = retailPrice * quantity;
        let profit = revenue - totalOrder;
        let breakeven = Math.ceil(totalOrder / retailPrice);
        this.roiMargin.textContent = margin.toFixed(2) + '%';
        this.roiMarkup.textContent = markup.toFixed(2) + '%';
        this.roiRevenue.textContent = this.formatMoney(revenue);
        this.roiProfit.textContent = this.formatMoney(profit);
        this.roiBreakeven.textContent = breakeven > 2 ? breakeven + ' Units' : breakeven + ' Unit';
      } else {
        this.roiMargin.textContent = '';
        this.roiMarkup.textContent = '';
        this.roiRevenue.textContent = '';
        this.roiProfit.textContent = '';
        this.roiBreakeven.textContent = '';
      }
    }
  });
}

if (!customElements.get('formulate-ingredients')) {
  customElements.define('formulate-ingredients', class FormulateIngredients extends BaseProductComponent {
    constructor() {
      super();

      this.ingredients = this.querySelectorAll('input.optional-ingredient');
      this.fragrances = this.querySelectorAll('input[name="properties[Fragrance]"]');
      this.fragranceSampleSet = this.querySelector('input[name="properties[Scent samples]"]')
      this.fragranceSampleSetCheckPath = this.querySelector('#custom-check');
      this.label = this.querySelector('#fragrance-label');
      this.accordion = this.closest('details');

      this.bindEvents();
    }

    bindEvents() {
      this.addEventListener('change', (e) => {
        const t = e.target;

        if ([...this.ingredients].includes(t)) {
          const checkedIngredients = this.querySelectorAll('input.optional-ingredient:checked');

          if (checkedIngredients.length > 2) {
            t.checked = false;
          } else {
            document.dispatchEvent(new Event('url:update'));
          }
        }
      });

      this.addEventListener('click', (e) => {
        const t = e.target;

        if ([...this.fragrances].includes(t)) {
          if (t.checked && t.dataset.wasChecked === "true") {
            t.checked = false;
            t.dataset.wasChecked = "false";
            t.dispatchEvent(new Event('change', { bubbles: true }));
          } else {
            // mark only this one as checked
            this.fragrances.forEach(r => r.dataset.wasChecked = "false");
            t.dataset.wasChecked = "true";
          }

          this.dispatchEvent(new Event('change', { bubbles: true }));
          document.dispatchEvent(new Event('url:update'));
        }
      });

      // Reset fragrance sample set
      if (!this.fragranceSampleSet || !this.fragranceSampleSetCheckPath) return;
      this.syncCheckmark();
      this.fragranceSampleSet.addEventListener('change', this.syncCheckmark.bind(this));
    }

    validate() {
      if (!this.label) return true;

      const isValid = !!this.querySelector('input[name="properties[Fragrance]"]:checked');

      this.label.classList.toggle('not-selected', !isValid);

      if (!isValid) this.accordion?.setAttribute('open', '');

      return isValid;
    }

    syncCheckmark = () => {
      this.fragranceSampleSetCheckPath.style.display = this.fragranceSampleSet.checked ? 'block' : 'none';
    };
  });
}

if (!customElements.get('packaging-style-select')) {
  customElements.define('packaging-style-select', class PackagingStyleSelect extends BaseProductComponent {
    constructor() {
      super();

      this.select = this.productMain.select;
      this.options = this.productMain.options;
      this.tableRows = this.productMain.tableRows;
      this.packagings = this.querySelectorAll('[name="properties[Packaging]"]');
      this.includesPackaging = this.querySelector('[name="properties[Includes packaging]"]');
      this.designLabelValue = this.querySelector('#design-label-value');
      this.designLabelTypes = this.querySelectorAll('[name="label_design_type"]');
      this.additionalBrandInputs = this.productMain.querySelector('#additional-brand-inputs');
      this.labelGuidelines = this.productMain.querySelector('#label-guidelines');
      this.designOptionsWrapper = this.querySelector('#design-options');
      this.designOptions = this.querySelectorAll('[name="label_design_option"]');
      this.packagingStyleLabel = this.querySelector('#packaging-style-label');
      this.stickerLabel = this.querySelector('#sticker-label');
      this.customDesignLabels = this.additionalBrandInputs.querySelectorAll('label.form__label:not(.optional)');
      this.accordion = this.closest('details');

      this.fontPicker = document.querySelector('.font-picker')
      this.fontTrigger = this.fontPicker.querySelector('.font-picker__trigger');
      this.fontDropdown = this.fontPicker.querySelector('.font-picker__dropdown');
      this.fontPreview = this.fontPicker.querySelector('#font-preview');
      this.font = this.fontPicker.querySelector('input[name="properties[Font]"]');

      this.logoFileInput = this.querySelector('#logo-file');
      this.logoFilePath = this.querySelector('#logo-path');
      this.preview = this.querySelector('#logo-preview');

      this.bindEvents();
    }

    bindEvents() {
      this.addEventListener('change', (e) => {
        const t = e.target;

        if (this.packagings && [...this.packagings].includes(t)) {
          document.dispatchEvent(new Event('url:update'));
          return;
        }

        if (t === this.includesPackaging) {
          this.filterOptions(t.checked);
          this.filterTable();
          return;
        }

        if ([...this.designLabelTypes].includes(t)) {
          this.toggleDesignOptions(t.value);
          return;
        }

        if ([...this.designOptions].includes(t)) {
          this.chooseDesignOption();

          const pastLabel = this.querySelector('.design-option.active');
          const activeLabel = t.closest('label');

          pastLabel?.classList.remove('active');
          activeLabel?.classList.add('active');
          return;
        }

        if (t === this.logoFileInput) {
          this.uploadLogo();
        }
      });

      this.addEventListener('click', (e) => {
        const t = e.target;

        if (t === this.fontTrigger) {
          this.fontDropdown.classList.toggle('hidden');
          return;
        }

        if (t.closest('.font-picker__dropdown li')) {
          const li = t.closest('li');
          const font = li.dataset.font;

          this.fontPreview.textContent = li.textContent;
          this.fontPreview.style.fontFamily = font;
          this.font.value = font;

          this.fontDropdown.classList.add('hidden');
          return;
        }

        if (!this.fontPicker.contains(t)) {
          this.fontDropdown.classList.add('hidden');
        }
      });
    }

    validate() {
      const isPackagingStyleValid = this.validatePackagingStyle();
      const isLabelDesignValid = this.validateLabelDesign();
      const isCustomDesignValid = this.querySelector('#line-item-label-design:checked') ? this.validateCustomDesign() : true;

      return isPackagingStyleValid && isLabelDesignValid && isCustomDesignValid;
    }

    validatePackagingStyle() {
      if (!this.packagingStyleLabel) return true;

      const isValid = !!this.querySelector('[name="properties[Packaging]"]:checked');

      this.packagingStyleLabel.classList.toggle('not-selected', !isValid);

      if (!isValid) this.accordion?.setAttribute('open', '');

      return isValid;
    }

    validateLabelDesign() {
      if (!this.stickerLabel) return true;

      const isValid = this.designLabelValue.value !== '';

      this.stickerLabel.classList.toggle('not-selected', !isValid);

      if (!isValid) this.accordion?.setAttribute('open', '');

      return isValid;
    }

    validateCustomDesign() {
      let isCustomDesignValid = true;

      for (const customDesignLabel of this.customDesignLabels) {
        const inputId = customDesignLabel.getAttribute('for');
        const input = this.querySelector(`#${inputId}`);

        if (!input) continue;

        const isValid = input.value !== '';

        customDesignLabel.classList.toggle('not-selected', !isValid);

        if (!isValid) {
          isCustomDesignValid = false;
        }
      }

      return isCustomDesignValid;
    }

    filterTable() {
      if (!this.tableRows || !this.options) return;

      console.log(this.tableRows.units);

      const units = this.tableRows.units.querySelectorAll('td:not([rowspan]):not([class])');
      const prices = this.tableRows.prices.querySelectorAll('td:not([rowspan]):not([class])');
      const discounts = this.tableRows.discounts.querySelectorAll('td:not([rowspan]):not([class])');

      for (let i = 0; i < this.options.length - 1; i++) {
        const option = this.options[i];

        units[i].hidden = option.hidden;
        prices[i].hidden = option.hidden;
        discounts[i].hidden = option.hidden;
      }
    }

    filterOptions(includePackaging) {
      if (!this.select || !this.options) return;

      this.options.forEach(option => {
        const hasPackaging = option.textContent.includes("with packaging");

        if (includePackaging) {
          option.hidden = !hasPackaging;
        } else {
          option.hidden = hasPackaging;
        }
      });

      const firstVisible = this.select.querySelector("option:not([hidden])");
      if (firstVisible) {
        firstVisible.selected = true;
        this.select.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }

    chooseDesignOption() {
      let selectedOption = this.designOptionsWrapper.querySelector('input:checked');
      if (selectedOption) {
        this.designLabelValue.value = selectedOption.value;
        document.dispatchEvent(new Event('url:update'));
      } else {
        this.designLabelValue.value = '';
      }
    }

    toggleDesignOptions(option) {
      if (option === 'choose') {
        this.labelGuidelines.hidden = true;
        this.additionalBrandInputs.hidden = false;
        this.designOptionsWrapper.hidden = false;
        this.designOptionsWrapper.querySelectorAll('input').forEach(input => {
          input.required = true;
        });

        this.customDesignLabels.forEach(customDesignLabel => {
          if (customDesignLabel.classList.contains('not-selected')) {
            customDesignLabel.classList.remove('not-selected');
          }
        })

        let selectedOption = this.designOptionsWrapper.querySelector('input:checked');

        if (selectedOption) {
          this.designLabelValue.value = selectedOption.value;
        } else {
          const firstOption = this.designOptionsWrapper.querySelector('input');
          const firstOptionLabel = this.designOptionsWrapper.querySelector('label');
          firstOption.checked = true;
          firstOption.dispatchEvent(new Event('change', { bubbles: true }));
          firstOptionLabel.dispatchEvent(new Event('click', { bubbles: true }));
          this.designLabelValue.value = firstOption.value;
        }
      } else {
        this.labelGuidelines.hidden = false;
        this.additionalBrandInputs.hidden = true;
        this.designOptionsWrapper.hidden = true;

        this.designOptionsWrapper.querySelectorAll('input').forEach(input => {
          input.checked = false;
          input.required = false;
        });

        this.designLabelValue.value = 'After checkout';
      }
      document.dispatchEvent(new Event('url:update'));
    }

    async uploadLogo() {
      if (!this.logoFileInput || !this.logoFilePath || !this.preview) return;

      const file = this.logoFileInput.files[0];
      if (!file) return;

      if (!file.type.startsWith('image/')) {
        alert('Please choose an image.');
        this.logoFileInput.value = '';
        return;
      }

      const MAX_SIZE_MB = 5;
      if (file.size > MAX_SIZE_MB * 1024 * 1024) {
        alert('Image should be 5MB max.');
        this.logoFileInput.value = '';
        return;
      }

      this.preview.innerHTML = 'Uploading...';

      const formData = new FormData();
      formData.append('file', file);

      try {
        const res = await fetch('/apps/cre8or-pacdora-wrapper/logo', {
          method: 'POST',
          body: formData
        });

        if (!res.ok) {
          throw new Error('Upload failed');
        }

        const data = await res.json();

        if (!data.path) {
          throw new Error('Invalid response');
        }

        this.logoFilePath.value = `/apps/cre8or-pacdora-wrapper${data.path}`;
        this.preview.innerHTML = `<img src="${this.logoFilePath.value}" alt="Uploaded logo" class="logo-upload-preview" />`;
      } catch (err) {
        console.error(err);
        alert('Error uploading logo');
        this.preview.innerHTML = '';
        this.logoFilePath.value = '';
        this.logoFileInput.value = '';
      }
    }
  });
}

if (!customElements.get('ingredient-list')) {
  customElements.define('ingredient-list', class IngredientList extends BaseProductComponent {
    constructor() {
      super();

      this.optionalCheckboxes = this.productMain.querySelectorAll('input.optional-ingredient[name="Ingredients[]"]');
      this.ingredientsInput = this.productMain.querySelector('input[name="properties[Ingredients]"]');
      this.fragranceRadios = this.productMain.querySelectorAll('input[name="properties[Fragrance]"]');
      this.baseSpan = this.querySelector('#base-ingredients');
      this.baseText = (this.baseSpan.textContent || '').trim().replace(/,\s*$/, '');
      this.inciInput = this.querySelector('[name="properties[_INCI LABEL]"]');
      this.inciIngredients = JSON.parse(this.dataset.inciIngredients || '[]');
      this.allergens = JSON.parse(this.dataset.allergens || '[]');

      this.bindEvents();
      this.updateList();
    }

    bindEvents() {
      this.productMain.addEventListener('change', (e) => {
        const t = e.target;

        if ([...this.optionalCheckboxes].includes(t) || [...this.fragranceRadios].includes(t)) {
          this.updateList();
        }
      });
    }

    updateList() {
      let ingredients = [];
      let additions = [];

      const checkedCount = [...this.optionalCheckboxes].filter(cb => cb.checked).length;
      if (checkedCount > 2) return;

      for (let i = 0; i < this.optionalCheckboxes.length; i++) {
        const cb = this.optionalCheckboxes[i];

        if (cb.checked) {
          const mapped = this.inciIngredients[i]
            ? this.inciIngredients[i].trim()
            : cb.value.trim();

          if (mapped) additions.push(mapped);
          ingredients.push(cb.value.trim());
        }
      }

      if (this.ingredientsInput) this.ingredientsInput.value = ingredients.join(', ');

      this.fragranceRadios.forEach((r, idx) => {
        if (r.checked) {
          const mapped = this.allergens[idx] ? this.allergens[idx].trim() : r.value.trim();
          if (mapped) additions.push(mapped);
        }
      });

      let finalString = additions.length ? this.baseText + ', ' + additions.join(', ') : this.baseText;
      if (!finalString.endsWith('.')) {
        finalString += '.';
      }
      this.baseSpan.textContent = finalString;
      if (this.inciInput) this.inciInput.value = finalString;
    }
  });
}